import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  Barcode,
  CheckCircle2,
  Plus,
  Sparkles,
  Trash2,
  Truck,
  PackageCheck,
} from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { Page } from '@/lib/paging'
import type { Branch, GoodsReceipt, GoodsReceiptSummary, Product, Supplier } from '@/lib/types'
import { formatQuantity, formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Modal, Select } from '@/components/ui/primitives'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { QuickCreate } from '@/components/ui/QuickCreate'
import { ProductFormFields, toDraft, useSaveProduct, type Draft } from '@/pages/ProductForm'
import { DeliveryLabels, type DeliveredItem } from '@/pages/DeliveryLabels'
import { useSession } from '@/stores/session'
import { t } from '@/i18n'

interface LineDraft {
  productId: number | ''
  quantity: string
  unitCost: string
  batchNumber: string
  expiryDate: string
  newPrice: string
}

const emptyLine: LineDraft = {
  productId: '',
  quantity: '',
  unitCost: '',
  batchNumber: '',
  expiryDate: '',
  newPrice: '',
}

/**
 * One bench for everything a delivery needs.
 *
 * Booking goods in, adding the product that arrived, opening an account for the supplier who
 * brought it and sticking a barcode on it are one job, done once, standing at the back door with
 * boxes on the floor. Splitting them across four screens meant abandoning a half-keyed delivery
 * to go and create a category, so all of it happens here — including the labels, which print
 * without leaving the page once the stock is in.
 */
export function ReceiveStockPage() {
  const queryClient = useQueryClient()
  const session = useSession((state) => state.session)

  const [supplierId, setSupplierId] = useState<number | ''>('')
  const [branchId, setBranchId] = useState<number | ''>(session?.branchId ?? '')
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [receiptDate, setReceiptDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [additionalCost, setAdditionalCost] = useState('0')
  /** Where the money for this delivery comes from. Credit means it stays owed. */
  const [paidFrom, setPaidFrom] = useState('Credit')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<LineDraft[]>([{ ...emptyLine }])
  const [error, setError] = useState<string | null>(null)

  /** Which line asked for a new product, so the one that gets created lands back on it. */
  const [addingProductFor, setAddingProductFor] = useState<number | null>(null)
  const [isAddingSupplier, setAddingSupplier] = useState(false)

  /** The delivery just posted, kept on screen so its labels can be printed. */
  const [posted, setPosted] = useState<{ number: string; items: DeliveredItem[] } | null>(null)
  /** A past delivery reopened to reprint its labels. Never editable. */
  const [reprinting, setReprinting] = useState<number | null>(null)

  const suppliers = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => apiClient.get<Supplier[]>('/api/suppliers'),
  })

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => apiClient.get<Branch[]>('/api/branches'),
  })

  const products = useQuery({
    queryKey: ['products', ''],
    queryFn: () =>
      apiClient.get<Page<Product>>('/api/products?pageSize=200').then((page) => page.items),
  })

  // Only needed to pay out of a till. Asked for whichever way the delivery is being paid, so
  // the answer is already there when someone switches to "the till" at the last moment.
  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () =>
      apiClient.get<{ id: number; name: string; currentShiftId: number | null }[]>('/api/registers'),
  })

  const openShiftId = registers.data?.find((register) => register.currentShiftId !== null)
    ?.currentShiftId ?? null

  const recent = useQuery({
    queryKey: ['goods-receipts'],
    queryFn: () =>
      apiClient
        .get<Page<GoodsReceiptSummary>>('/api/goods-receipts?pageSize=20')
        .then((page) => page.items),
  })

  const past = useQuery({
    queryKey: ['goods-receipt', reprinting],
    queryFn: () => apiClient.get<GoodsReceipt>(`/api/goods-receipts/${reprinting}`),
    enabled: reprinting !== null,
  })

  const productsById = new Map(products.data?.map((product) => [product.id, product]))

  const goodsTotal = lines.reduce(
    (sum, line) => sum + Number(line.quantity || 0) * Number(line.unitCost || 0),
    0,
  )
  const grandTotal = goodsTotal + Number(additionalCost || 0)

  const generateInvoiceNumber = useMutation({
    mutationFn: () => apiClient.get<{ number: string }>('/api/goods-receipts/next-number'),
    onSuccess: (next) => setInvoiceNumber(next.number),
    onError: () => setError('Could not work out a number.'),
  })

  const submit = useMutation({
    mutationFn: async () => {
      const kept = lines.filter((line) => line.productId !== '' && Number(line.quantity) > 0)

      const body = {
        branchId: branchId === '' ? null : Number(branchId),
        supplierId: Number(supplierId),
        supplierInvoiceNumber: invoiceNumber.trim() || null,
        receiptDate,
        additionalCostUsd: Number(additionalCost || 0),
        isOnCredit: paidFrom === 'Credit',
        paidFrom,
        shiftId: paidFrom === 'CashDrawer' ? openShiftId : null,
        lbpRateUsed: null,
        notes: notes.trim() || null,
        lines: kept.map((line) => ({
          productId: Number(line.productId),
          quantity: Number(line.quantity),
          unitCostUsd: Number(line.unitCost || 0),
          batchNumber: line.batchNumber.trim() || null,
          expiryDate: line.expiryDate || null,
          newPriceUsd: line.newPrice === '' ? null : Number(line.newPrice),
        })),
      }

      // Two steps on purpose: the draft is saved first so nothing is lost if posting is
      // rejected, then posting is the deliberate act that moves stock.
      const draft = await apiClient.post<{ id: number }>('/api/goods-receipts', body)
      const receipt = await apiClient.post<{ number: string; totalCostUsd: number }>(
        `/api/goods-receipts/${draft.id}/post`,
        // Posting is what moves the money, so the till has to be named again here — the draft
        // is only a description until it is posted.
        { shiftId: paidFrom === 'CashDrawer' ? openShiftId : null },
      )

      return {
        number: receipt.number,
        items: kept.map((line) => ({
          productId: Number(line.productId),
          quantity: Number(line.quantity),
        })),
      }
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['products'] })
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
      await queryClient.invalidateQueries({ queryKey: ['goods-receipts'] })

      // Deliberately no navigation. The boxes are still on the floor and the labels still have
      // to go on them; sending the clerk back to the inventory list here is what made labelling
      // a separate errand that half of them never ran.
      setPosted(result)
      setError(null)
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? (caught.detail ?? caught.message)
          : 'Could not post the delivery.',
      ),
  })

  function updateLine(index: number, patch: Partial<LineDraft>) {
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)))
  }

  function startAnother() {
    setPosted(null)
    setLines([{ ...emptyLine }])
    setInvoiceNumber('')
    setAdditionalCost('0')
    setNotes('')
    setPaidFrom('Credit')
    setError(null)
  }

  const canSubmit =
    supplierId !== '' &&
    lines.some((line) => line.productId !== '' && Number(line.quantity) > 0) &&
    // Paying from a till nobody has opened cannot work, and the server refuses it — said here
    // so the clerk finds out before keying a whole delivery rather than after.
    (paidFrom !== 'CashDrawer' || openShiftId !== null)

  /* ------------------------------------------------- reprinting a past one */

  if (reprinting !== null) {
    return (
      <div className="pb-page mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
        <button
          type="button"
          onClick={() => setReprinting(null)}
          className="mb-4 inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('Back to receiving')}
        </button>

        <h1 className="text-xl font-semibold">
          Labels for {past.data?.number ?? 'that delivery'}
        </h1>
        <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
          A posted delivery is a record of what the goods cost and cannot be changed. Its labels
          can be reprinted as often as you like.
        </p>

        <div className="mt-5">
          <DeliveryLabels
            items={(past.data?.lines ?? []).map((line) => ({
              productId: line.productId,
              quantity: line.quantity,
            }))}
            products={products.data ?? []}
            title={t('What came in')}
            description={
              past.data
                ? `${past.data.supplierName ?? 'Supplier'} · ${past.data.receiptDate.slice(0, 10)}`
                : undefined
            }
          />
        </div>
      </div>
    )
  }

  /* ------------------------------------------------------ posting complete */

  if (posted) {
    return (
      <div className="pb-page mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
        <div className="surface-raised mb-5 flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border p-4">
          <CheckCircle2 className="h-6 w-6 text-success-500" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">{posted.number} is in stock</p>
            <p className="text-sm text-[var(--text-secondary)]">
              {posted.items.length} {posted.items.length === 1 ? 'item' : 'items'}, at{' '}
              {formatUsd(grandTotal)} landed. Label them below.
            </p>
          </div>
          <Button variant="secondary" onClick={startAnother}>
            <Plus className="h-4 w-4" />
            {t('Another delivery')}
          </Button>
          <Link to="/inventory">
            <Button variant="ghost">{t('Done')}</Button>
          </Link>
        </div>

        <DeliveryLabels
          items={posted.items}
          products={products.data ?? []}
          title={t('Print the labels')}
          description={t('Copies default to how many came in. Anything without a code gets one here.')}
        />
      </div>
    )
  }

  /* --------------------------------------------------------- the main form */

  return (
    <div className="pb-page mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
      <Link
        to="/inventory"
        className="mb-4 inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('Back to inventory')}
      </Link>

      <h1 className="text-xl font-semibold">{t('Receive stock')}</h1>
      <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
        Add products, suppliers and barcodes as you go. Freight and customs are spread across the
        lines by value, so each batch carries its true landed cost.
      </p>

      <section className="surface-raised mt-5 grid gap-4 rounded-[var(--radius-card)] border p-4 sm:grid-cols-2">
        <div>
          <SearchableSelect
            label={t('Supplier')}
            value={supplierId}
            placeholder={t('Choose a supplier…')}
            options={(suppliers.data ?? []).map((supplier) => ({
              value: supplier.id,
              label: supplier.name,
            }))}
            onChange={(picked) => setSupplierId(picked === '' ? '' : Number(picked))}
          />
          <Button
            variant="ghost"
            size="sm"
            className="mt-1"
            onClick={() => setAddingSupplier(true)}
          >
            <Plus className="h-4 w-4" />
            {t('New supplier')}
          </Button>
        </div>

        {/* An Admin is not tied to a branch, so they have to say where the goods landed. */}
        {session?.branchId == null && (
          <Select
            label={t('Received at')}
            value={branchId}
            onChange={(e) => setBranchId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">{t('Choose a branch…')}</option>
            {branches.data?.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.displayName}
              </option>
            ))}
          </Select>
        )}

        <div>
          <Input
            label={t('Invoice no.')}
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            placeholder="INV-2026-114"
            className="tabular"
          />
          {/* Plenty of local suppliers hand over no paperwork at all, and a clerk inventing a
              reference per delivery invents a different scheme each time. */}
          <Button
            variant="ghost"
            size="sm"
            className="mt-1"
            disabled={generateInvoiceNumber.isPending}
            onClick={() => generateInvoiceNumber.mutate()}
          >
            <Sparkles className="h-4 w-4" />
            {t('Generate one')}
          </Button>
        </div>

        <Input
          label={t('Delivery date')}
          type="date"
          value={receiptDate}
          onChange={(e) => setReceiptDate(e.target.value)}
        />
      </section>

      <section className="mt-5">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">{t('Items')}</h2>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setLines((current) => [...current, { ...emptyLine }])}
          >
            <Plus className="h-4 w-4" />
            {t('Add line')}
          </Button>
        </div>

        <div className="flex flex-col gap-3">
          {lines.map((line, index) => {
            const product = line.productId === '' ? null : productsById.get(Number(line.productId))
            const lineTotal = Number(line.quantity || 0) * Number(line.unitCost || 0)

            return (
              <div key={index} className="surface-raised rounded-[var(--radius-card)] border p-3">
                <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr_auto]">
                  <div>
                    <SearchableSelect
                      label={t('Product')}
                      value={line.productId}
                      placeholder={t('Choose a product…')}
                      options={(products.data ?? []).map((option) => ({
                        value: option.id,
                        label: option.name,
                        hint: option.sku,
                      }))}
                      onChange={(picked) => {
                        // The cost follows the product, always — it is a fact about the item
                        // just chosen, not a number the line is carrying. Keeping whatever was
                        // there left the previous product's cost sitting under a new name, and
                        // the clerk only finds out when the batch is already posted at it.
                        //
                        // A product with no cost yet clears the box rather than filling in a
                        // zero, so it reads as something to type instead of something agreed.
                        const cost = productsById.get(Number(picked))?.costUsd ?? 0

                        updateLine(index, {
                          productId: picked === '' ? '' : Number(picked),
                          unitCost: picked === '' || cost <= 0 ? '' : String(cost),
                        })
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-1"
                      disabled={addingProductFor !== null}
                      onClick={() => setAddingProductFor(index)}
                    >
                      <Plus className="h-4 w-4" />
                      {t('New product')}
                    </Button>
                  </div>

                  <Input
                    label={t('Quantity')}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.001"
                    value={line.quantity}
                    onChange={(e) => updateLine(index, { quantity: e.target.value })}
                    className="tabular"
                  />

                  <Input
                    label={t('Unit cost')}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={line.unitCost}
                    onChange={(e) => updateLine(index, { unitCost: e.target.value })}
                    className="tabular"
                  />

                  <div className="flex items-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('Remove line')}
                      onClick={() => setLines((c) => c.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  <Input
                    label={t('Batch no.')}
                    value={line.batchNumber}
                    onChange={(e) => updateLine(index, { batchNumber: e.target.value })}
                    placeholder={t('Optional')}
                  />
                  <Input
                    label={t('Expiry')}
                    type="date"
                    value={line.expiryDate}
                    onChange={(e) => updateLine(index, { expiryDate: e.target.value })}
                    // Required by the server for perishables; flagged here so the clerk finds
                    // out before posting rather than after.
                    error={
                      product?.trackExpiry && !line.expiryDate
                        ? 'This product needs an expiry date'
                        : undefined
                    }
                  />
                  <Input
                    label={t('New shelf price')}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={line.newPrice}
                    onChange={(e) => updateLine(index, { newPrice: e.target.value })}
                    className="tabular"
                    placeholder={product ? String(product.priceUsd) : 'Leave blank to keep'}
                  />
                </div>

                {product && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[var(--text-secondary)]">
                    <Barcode className="h-3.5 w-3.5" />
                    {product.primaryBarcode ? (
                      <span className="tabular">{product.primaryBarcode}</span>
                    ) : (
                      <Badge tone="warn">{t('No barcode — one can be minted after posting')}</Badge>
                    )}
                    {lineTotal > 0 && (
                      <span className="tabular ml-auto">Line total {formatUsd(lineTotal)}</span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>

      <section className="surface-raised mt-5 rounded-[var(--radius-card)] border p-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('Freight, customs and handling (USD)')}
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={additionalCost}
            onChange={(e) => setAdditionalCost(e.target.value)}
            className="tabular"
            hint={t('Spread across the lines by value')}
          />
          <div>
            {/* Posting is the moment the goods arrive and the moment the money leaves, so this
                has to be answered before it. Paying from the till or the safe writes the cash
                movement; the rest leave the drawer alone. */}
            <Select
              label={t('Paid from')}
              value={paidFrom}
              onChange={(e) => setPaidFrom(e.target.value)}
            >
              <option value="Credit">{t('On credit — pay the supplier later')}</option>
              <option value="CashDrawer">{t('The till')}</option>
              <option value="Safe">{t('The safe')}</option>
              <option value="Bank">{t('Bank transfer')}</option>
              <option value="Card">{t('Card')}</option>
            </Select>

            <p className="mt-1.5 text-xs text-[var(--text-secondary)]">
              {paidFrom === 'Credit'
                ? 'Nothing leaves the shop now. It shows in what you owe suppliers until you settle it.'
                : paidFrom === 'CashDrawer'
                  ? openShiftId
                    ? `${formatUsd(grandTotal)} comes out of the open till when this is posted.`
                    : 'No till is open, so there is nowhere to take the money from. Open one, or pay another way.'
                  : paidFrom === 'Safe'
                    ? `${formatUsd(grandTotal)} comes out of the safe when this is posted.`
                    : 'Recorded as paid. Neither the till nor the safe is touched.'}
            </p>
          </div>
        </div>

        <Input
          label={t('Notes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          wrapperClassName="mt-4"
          placeholder={t('Optional')}
        />

        <dl className="mt-4 flex flex-col gap-1 border-t border-[var(--border-subtle)] pt-4 text-sm">
          <div className="flex justify-between">
            <dt className="text-[var(--text-secondary)]">{t('Goods')}</dt>
            <dd className="tabular">{formatUsd(goodsTotal)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--text-secondary)]">{t('Additional costs')}</dt>
            <dd className="tabular">{formatUsd(Number(additionalCost || 0))}</dd>
          </div>
          <div className="flex justify-between text-base font-semibold">
            <dt>{t('Total into stock')}</dt>
            <dd className="tabular">{formatUsd(grandTotal)}</dd>
          </div>
        </dl>

        {error && (
          <p
            role="alert"
            className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500"
          >
            {error}
          </p>
        )}

        <Button
          size="lg"
          full
          className="mt-4"
          disabled={!canSubmit || submit.isPending}
          onClick={() => {
            setError(null)
            submit.mutate()
          }}
        >
          <PackageCheck className="h-5 w-5" />
          {submit.isPending ? 'Posting…' : 'Post into stock'}
        </Button>
        <p className="mt-2 text-center text-xs text-[var(--text-secondary)]">
          {t('Posting creates the batches and fixes their cost. It cannot be undone.')}
        </p>
      </section>

      <section className="mt-8">
        <h2 className="mb-2 text-sm font-semibold">{t('Recent deliveries')}</h2>
        {recent.data?.length === 0 ? (
          <EmptyState
            icon={Truck}
            title={t('No deliveries yet')}
            description={t('Once you post a delivery it appears here with its landed total.')}
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {recent.data?.slice(0, 10).map((receipt) => (
              <li key={receipt.id}>
                {/* Opens the labels, never the delivery. A posted receipt is what the goods cost
                    and has to stay exactly as recorded; reprinting a sticker does not touch it. */}
                <button
                  type="button"
                  disabled={receipt.status !== 'Posted'}
                  onClick={() => setReprinting(receipt.id)}
                  className="surface-raised flex w-full items-center gap-3 rounded-[var(--radius-card)] border p-3 text-left transition-colors enabled:hover:border-[var(--accent)] disabled:opacity-60"
                >
                  <div className="min-w-0 flex-1">
                    <p className="tabular truncate text-sm font-medium">{receipt.number}</p>
                    <p className="truncate text-xs text-[var(--text-secondary)]">
                      {receipt.supplierName} · {receipt.receiptDate.slice(0, 10)}
                    </p>
                    {/* What came in, and how much of it. Enough to pick the right delivery to
                        reprint from without opening three of them to find out. */}
                    <ul className="mt-1 flex flex-col gap-0.5">
                      {(receipt.lines ?? []).map((line, index) => (
                        <li
                          key={index}
                          className="flex gap-2 text-xs text-[var(--text-secondary)]"
                        >
                          <span className="tabular w-12 shrink-0 text-right font-medium">
                            {formatQuantity(line.quantity)}
                          </span>
                          <span className="truncate">{line.productName ?? 'Deleted product'}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Badge tone={receipt.status === 'Posted' ? 'success' : 'neutral'}>
                    {receipt.status}
                  </Badge>
                  <p className="tabular w-24 shrink-0 text-right text-sm font-medium">
                    {formatUsd(receipt.totalCostUsd)}
                  </p>
                  {receipt.status === 'Posted' && (
                    <Barcode className="h-4 w-4 shrink-0 text-[var(--text-secondary)]" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Everything that can be missing mid-delivery, creatable without leaving it. */}
      <NewProductDialog
        open={addingProductFor !== null}
        onClose={() => setAddingProductFor(null)}
        onCreated={(created, cost) => {
          if (addingProductFor !== null) {
            // The cost as well as the product. It was already keyed on the form a moment ago,
            // and leaving the line's cost blank meant retyping it or — worse — posting a
            // delivery at zero, which is a batch whose cost is a lie for as long as it lasts.
            updateLine(addingProductFor, { productId: created.id, unitCost: cost })
          }
          setAddingProductFor(null)
        }}
      />

      <QuickCreate
        open={isAddingSupplier}
        what="supplier"
        path="/api/suppliers"
        invalidates="suppliers"
        onClose={() => setAddingSupplier(false)}
        onCreated={(created) => {
          setSupplierId(created.id)
          setAddingSupplier(false)
        }}
      />

    </div>
  )
}

/**
 * The whole of "new product", over the delivery that needs it.
 *
 * Same fields and same saving as the editor on the inventory list — this is where they are
 * actually reached from, because a product that has never been delivered has no real cost.
 * Saving drops it onto the line that asked, with the cost already keyed here, so the clerk
 * carries straight on typing quantities rather than hunting the new item out of a list of
 * hundreds and then retyping what it cost.
 */
function NewProductDialog({
  open,
  onCreated,
  onClose,
}: {
  open: boolean
  onCreated: (created: { id: number; name: string }, cost: string) => void
  onClose: () => void
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(null, 11))
  const { save, error, setError } = useSaveProduct(null)

  const tax = useQuery({
    queryKey: ['settings', 'tax'],
    queryFn: () =>
      apiClient.get<{ vatEnabled: boolean; defaultVatRate: number }>('/api/settings/tax'),
    enabled: open,
  })

  useEffect(() => {
    // A clean form each time it opens, on the shop's own VAT rate.
    if (open) {
      setDraft(toDraft(null, (tax.data?.defaultVatRate ?? 0.11) * 100))
      setError(null)
    }
  }, [open, tax.data?.defaultVatRate, setError])

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={t('New product')}
      description={t('It goes onto the line you were filling in, with the cost you enter here.')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={() =>
              save.mutate(draft, { onSuccess: (created) => onCreated(created, draft.costUsd) })
            }
            disabled={save.isPending || draft.name.trim() === '' || draft.categoryId === ''}
          >
            {save.isPending ? 'Saving…' : 'Save and use on this line'}
          </Button>
        </>
      }
    >
      <ProductFormFields
        draft={draft}
        onChange={setDraft}
        vatEnabled={tax.data?.vatEnabled !== false}
      />

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500"
        >
          {error}
        </p>
      )}
    </Modal>
  )
}
